/**
 * When an audio file was recorded, read from the file itself rather than from
 * `lastModified` - which is when it was last copied, AirDropped or downloaded
 * as often as when it was made. Several files of one lecture are put in order
 * with this, and an upload is placed on the timetable by it.
 *
 * Sources, best first:
 *  1. `com.apple.quicktime.creationdate` - the start, with its time zone.
 *  2. The container's date tag (`©day`, ID3 `TDRC`, Vorbis `DATE`, WAV
 *     `ICRD`), when it carries a time and not just a day.
 *  3. A WAV's broadcast extension (`bext`) - what field recorders write.
 *  4. The MP4 movie header's creation time.
 *  5. A timestamp in the file name - `20261002_081512.m4a` and the like.
 *
 * Everything here reads headers only, so it is quick for a two-hour file and
 * for a 4 GB video alike. Any of it failing just means "unknown".
 */

export interface FileTiming {
  /** Epoch ms the recording started, when the file says. */
  recordedAt: number | null;
  durationMs: number | null;
}

/** Seconds from 1904-01-01, the MP4 epoch, to 1970-01-01. */
const MP4_EPOCH_OFFSET_S = 2_082_844_800;
const MP4_EXTENSIONS = new Set(["m4a", "mp4", "m4v", "mov", "3gp"]);

/** Before this a timestamp is a zeroed field or a clock that was never set. */
const EARLIEST_PLAUSIBLE = Date.UTC(2000, 0, 1);

function plausible(ms: number | null | undefined): number | null {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return null;
  if (ms < EARLIEST_PLAUSIBLE || ms > Date.now() + 24 * 3_600_000) return null;
  return ms;
}

function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot + 1).toLowerCase();
}

async function bytesAt(file: Blob, offset: number, length: number): Promise<DataView> {
  return new DataView(await file.slice(offset, offset + length).arrayBuffer());
}

function fourCC(view: DataView, offset: number): string {
  return String.fromCharCode(
    view.getUint8(offset),
    view.getUint8(offset + 1),
    view.getUint8(offset + 2),
    view.getUint8(offset + 3),
  );
}

/** First box of `type` between `start` and `end`, as [content start, box end]. */
async function findBox(
  file: Blob,
  start: number,
  end: number,
  type: string,
): Promise<[number, number] | null> {
  let offset = start;
  // `moov` sits after `mdat` in a file written while recording, so this walks
  // a handful of headers and skips the media data by its size.
  for (let guard = 0; guard < 64 && offset + 8 <= end; guard++) {
    const head = await bytesAt(file, offset, 16);
    if (head.byteLength < 8) return null;
    let size = head.getUint32(0);
    const name = fourCC(head, 4);
    let header = 8;
    if (size === 1) {
      if (head.byteLength < 16) return null;
      size = Number(head.getBigUint64(8));
      header = 16;
    } else if (size === 0) {
      size = end - offset;
    }
    if (size < header) return null;
    if (name === type) return [offset + header, Math.min(offset + size, end)];
    offset += size;
  }
  return null;
}

async function mp4CreationTime(file: File): Promise<number | null> {
  const moov = await findBox(file, 0, file.size, "moov");
  if (moov === null) return null;
  const mvhd = await findBox(file, moov[0], moov[1], "mvhd");
  if (mvhd === null) return null;
  const view = await bytesAt(file, mvhd[0], 12);
  if (view.byteLength < 8) return null;
  const seconds =
    view.getUint8(0) === 1 && view.byteLength >= 12
      ? Number(view.getBigUint64(4))
      : view.getUint32(4);
  if (seconds === 0) return null;
  return plausible((seconds - MP4_EPOCH_OFFSET_S) * 1_000);
}

/** `bext` OriginationDate + OriginationTime, in the recorder's local time. */
async function wavOriginationTime(file: File): Promise<number | null> {
  const riff = await bytesAt(file, 0, 12);
  if (riff.byteLength < 12 || fourCC(riff, 0) !== "RIFF" || fourCC(riff, 8) !== "WAVE") {
    return null;
  }
  let offset = 12;
  for (let guard = 0; guard < 32 && offset + 8 <= file.size; guard++) {
    const head = await bytesAt(file, offset, 8);
    const size = head.getUint32(4, true);
    if (fourCC(head, 0) === "bext") {
      // Description (256) + Originator (32) + OriginatorReference (32).
      const raw = new TextDecoder("latin1").decode(
        await file.slice(offset + 8 + 320, offset + 8 + 338).arrayBuffer(),
      );
      const match = /^(\d{4})\D(\d{2})\D(\d{2})(\d{2})\D(\d{2})\D(\d{2})/.exec(raw);
      if (match === null) return null;
      const [, y, mo, d, h, mi, s] = match.map(Number);
      return plausible(new Date(y, mo - 1, d, h, mi, s).getTime());
    }
    offset += 8 + size + (size % 2);
  }
  return null;
}

/**
 * `20261002_081512`, `2026-10-02 08.15.12`, `REC20261002-0815`... read as
 * local time, which is what a phone or recorder names its files in.
 */
export function timeFromName(name: string): number | null {
  const match =
    /(20\d{2})[-_.]?(0[1-9]|1[0-2])[-_.]?(0[1-9]|[12]\d|3[01])(?:[ _T.-]*(?:at[ _]?)?)([01]\d|2[0-3])[-_.:h]?([0-5]\d)(?:[-_.:m]?([0-5]\d))?/i.exec(
      name,
    );
  if (match === null) return null;
  const [y, mo, d, h, mi] = match.slice(1, 6).map(Number);
  const s = match[6] === undefined ? 0 : Number(match[6]);
  return plausible(new Date(y, mo - 1, d, h, mi, s).getTime());
}

/** A date tag that is only a day parses as midnight UTC and says nothing. */
function hasTimeOfDay(date: Date): boolean {
  return (
    date.getUTCHours() !== 0 || date.getUTCMinutes() !== 0 || date.getUTCSeconds() !== 0
  );
}

/** What mediabunny can tell: the duration, and the tags it already parses. */
async function fromContainer(
  file: File,
): Promise<{ recordedAt: number | null; durationMs: number | null }> {
  const { ALL_FORMATS, BlobSource, Input } = await import("mediabunny");
  const input = new Input({ source: new BlobSource(file), formats: ALL_FORMATS });
  try {
    let durationMs: number | null = null;
    try {
      const seconds = await input.computeDuration();
      durationMs = Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds * 1_000) : null;
    } catch {
      // Unknown container - the server measures it anyway.
    }
    let recordedAt: number | null = null;
    try {
      const tags = await input.getMetadataTags();
      const apple = tags.raw?.["com.apple.quicktime.creationdate"];
      if (typeof apple === "string") recordedAt = plausible(Date.parse(apple));
      if (recordedAt === null && tags.date !== undefined && hasTimeOfDay(tags.date)) {
        recordedAt = plausible(tags.date.getTime());
      }
    } catch {
      // No tags is the common case.
    }
    return { recordedAt, durationMs };
  } catch {
    return { recordedAt: null, durationMs: null };
  } finally {
    input.dispose();
  }
}

export async function readFileTiming(file: File): Promise<FileTiming> {
  const container = await fromContainer(file).catch(() => ({
    recordedAt: null,
    durationMs: null,
  }));
  let recordedAt = container.recordedAt;
  const extension = extensionOf(file.name);
  if (recordedAt === null && (extension === "wav" || file.type === "audio/wav")) {
    recordedAt = await wavOriginationTime(file).catch(() => null);
  }
  if (
    recordedAt === null &&
    (MP4_EXTENSIONS.has(extension) || /mp4|quicktime|3gpp/.test(file.type))
  ) {
    recordedAt = await mp4CreationTime(file).catch(() => null);
  }
  recordedAt ??= timeFromName(file.name);
  return { recordedAt, durationMs: container.durationMs };
}

/**
 * Where a file sits in time for ordering: its own timestamp, else the same
 * guess the server makes - modified time minus length.
 */
export function startOf(file: File, timing: FileTiming): number {
  if (timing.recordedAt !== null) return timing.recordedAt;
  return file.lastModified - (timing.durationMs ?? 0);
}
